import React from 'react';
import type { Request } from '@features/requests/types';
import { RequestStatus } from '@features/requests/types';
import styles from './RequestDetails.module.css';
import {
    RequestHeader,
    RequestOverview,
    RequestCalendar,
    RequestInfo,
    RequestNotes,
    RequestAttachments,
    RequestHistory,
    RequestMeta,
    RequestFooter,
} from './components';

/**
 * Props for RequestDetails component
 */
export interface RequestDetailsProps {
    /** The request to display */
    request: Request;
    /** Callback when user goes back */
    onBack: () => void;
    /** Callback when user wants to edit (only for DRAFT and DECLINED) */
    onEdit?: () => void;
    /** Callback when user wants to submit (DRAFT) or resubmit (DECLINED) */
    onSubmit?: () => void;
    /** Callback when user wants to cancel (only for DELIVERED) */
    onCancel?: () => void;
}

/**
 * Displays detailed information about a request
 */
export function RequestDetails({
    request,
    onBack,
    onEdit,
    onSubmit,
    onCancel,
}: RequestDetailsProps): React.ReactElement {
    const showHistory = request.status !== RequestStatus.DRAFT;
    const hasSelectedDates = !!request.startDate && !!request.endDate;

    return (
        <div className={styles.container}>
            <RequestHeader
                request={request}
                onBack={onBack}
            />

            <div className={styles.content}>
                <RequestOverview request={request} />

                {hasSelectedDates && <RequestCalendar request={request} />}

                <RequestInfo request={request} />

                <RequestNotes request={request} />

                <RequestAttachments request={request} />

                {showHistory && request.history.length > 0 && (
                    <RequestHistory request={request} />
                )}

                <RequestMeta request={request} />
            </div>

            <RequestFooter
                request={request}
                onBack={onBack}
                onEdit={onEdit}
                onSubmit={onSubmit}
                onCancel={onCancel}
                hasSelectedDates={hasSelectedDates}
            />
        </div>
    );
}
